/**
 * DAoC Threat Table Engine - Real-time aggro management per monster
 */

import { EntityState } from '../../core/Types';
import { EntityRegistry } from '../../core/EntityRegistry';
import { EventBus } from '../../core/EventBus';

export class ThreatTableEngine {
  /**
   * Add direct threat from damage or active abilities
   */
  public addThreat(monster: EntityState, targetId: string, amount: number): void {
    if (monster.isDead) return;
    if (!monster.threatTable) {
      monster.threatTable = {};
    }

    const current = monster.threatTable[targetId] || 0;
    monster.threatTable[targetId] = current + amount;

    EventBus.emit('THREAT_CHANGED', {
      monsterId: monster.id,
      targetId,
      newThreat: monster.threatTable[targetId],
    });
  }

  /**
   * DAoC Style Taunt: sets target's threat to (current highest + bonus)
   */
  public taunt(monster: EntityState, targetId: string, bonus = 800): void {
    if (monster.isDead) return;
    if (!monster.threatTable) {
      monster.threatTable = {};
    }

    let highest = 0;
    for (const id in monster.threatTable) {
      if (monster.threatTable[id] > highest) {
        highest = monster.threatTable[id];
      }
    }

    monster.threatTable[targetId] = highest + bonus;
    monster.targetId = targetId;

    EventBus.emit('THREAT_CHANGED', {
      monsterId: monster.id,
      targetId,
      newThreat: monster.threatTable[targetId],
    });
  }

  /**
   * Healing generates threat on all engaged monsters (0.6x heal amount)
   */
  public distributeHealingThreat(
    engagedMonsters: EntityState[],
    healerId: string,
    healAmount: number
  ): void {
    const healThreat = Math.floor(healAmount * 0.6);
    if (healThreat <= 0) return;

    for (const monster of engagedMonsters) {
      if (!monster.isDead && monster.inCombat) {
        this.addThreat(monster, healerId, healThreat);
      }
    }
  }

  /**
   * Returns the entity with the highest threat that is alive and valid
   */
  public getHighestThreatTarget(
    monster: EntityState,
    registry: EntityRegistry
  ): EntityState | null {
    if (!monster.threatTable) return null;

    let bestTarget: EntityState | null = null;
    let maxThreat = -1;

    for (const targetId in monster.threatTable) {
      const threat = monster.threatTable[targetId];
      if (threat <= 0) continue;

      const targetEntity = registry.get(targetId);
      if (targetEntity && !targetEntity.isDead) {
        if (threat > maxThreat) {
          maxThreat = threat;
          bestTarget = targetEntity;
        }
      } else {
        // Target is dead or invalid, zero out
        delete monster.threatTable[targetId];
      }
    }

    return bestTarget;
  }

  /**
   * Remove target when dead or teleported
   */
  public removeTarget(monster: EntityState, targetId: string): void {
    if (monster.threatTable && monster.threatTable[targetId]) {
      delete monster.threatTable[targetId];
      if (monster.targetId === targetId) {
        monster.targetId = null;
      }
    }
  }

  /**
   * Get total accumulated threat generated by an entity across all engaged monsters
   */
  public getTotalThreat(entityId: string, monsters: EntityState[]): number {
    let total = 0;
    for (const m of monsters) {
      if (m.threatTable && m.threatTable[entityId]) {
        total += m.threatTable[entityId];
      }
    }
    return total;
  }
}

export const ThreatTable = new ThreatTableEngine();
